import React from 'react';
import { FormattedMessage } from 'react-intl';
import Button from './ui/Button';
import LoadingIndicator from './LoadingIndicator';
import { useStgAuth } from '../hooks/useStgAuth';

const LOGO_NORMAL_SRC = `${process.env.PUBLIC_URL}/assets/images/jp_jtc_logo_normal.svg`;

const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
    <path fill="none" d="M0 0h48v48H0z" />
  </svg>
);

const AuthScreen = ({ children }) => (
  <div className="min-h-screen min-h-dvh bg-background flex items-center justify-center px-6 py-12 font-body">
    <div className="relative w-full max-w-[420px]">
      <div className="absolute bottom-full left-0 right-0 mb-5 flex justify-center">
        <img
          src={LOGO_NORMAL_SRC}
          alt="日本トレカセンター"
          className="h-10 w-auto object-contain"
        />
      </div>
      {children}
    </div>
  </div>
);

const AuthCard = ({ title, description, children }) => (
  <div className="w-full max-w-[420px] rounded-2xl border border-border bg-card p-8 text-center shadow-[0_8px_32px_rgba(0,0,0,0.06)]">
    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.24em] text-primary">
      <FormattedMessage id="stgAuth.eyebrow" defaultMessage="STAGING" />
    </p>
    <h1 className="mb-3 text-xl font-bold text-foreground">{title}</h1>
    <p className="mb-7 text-sm leading-6 text-muted-foreground">
      {description}
    </p>
    <div className="flex flex-col items-center gap-3">{children}</div>
  </div>
);

const GoogleButton = ({ onClick, children }) => (
  <Button
    type="button"
    variant="outline"
    icon={GoogleIcon}
    onClick={onClick}
    className="w-full border-[#E0E0E0] bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900"
  >
    {children}
  </Button>
);

const StgAuthGate = ({ children }) => {
  const { status, error, signInWithGoogle, signOut } = useStgAuth();

  if (status === 'disabled' || status === 'authorized') {
    return children;
  }

  if (status === 'loading') {
    return (
      <div className="min-h-screen min-h-dvh bg-background relative">
        <LoadingIndicator
          show
          label={
            <FormattedMessage id="stgAuth.loading" defaultMessage="認証中…" />
          }
        />
      </div>
    );
  }

  if (status === 'denied') {
    return (
      <AuthScreen>
        <AuthCard
          title={
            <FormattedMessage
              id="stgAuth.denied.title"
              defaultMessage="アクセスできません"
            />
          }
          description={
            <FormattedMessage
              id="stgAuth.denied.description"
              defaultMessage="この Google アカウントにはステージング環境の閲覧権限がありません。サインアウトして別のアカウントでお試しください。"
            />
          }
        >
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={signOut}
          >
            <FormattedMessage
              id="stgAuth.denied.signOut"
              defaultMessage="サインアウト"
            />
          </Button>
        </AuthCard>
      </AuthScreen>
    );
  }

  if (status === 'error') {
    return (
      <AuthScreen>
        <AuthCard
          title={
            <FormattedMessage
              id="stgAuth.error.title"
              defaultMessage="認証エラー"
            />
          }
          description={
            error || (
              <FormattedMessage
                id="stgAuth.error.description"
                defaultMessage="Google 認証の準備に失敗しました。もう一度お試しください。"
              />
            )
          }
        >
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => window.location.reload()}
          >
            <FormattedMessage
              id="stgAuth.error.retry"
              defaultMessage="再試行"
            />
          </Button>
        </AuthCard>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen>
      <AuthCard
        title={
          <FormattedMessage
            id="stgAuth.login.title"
            defaultMessage="ログインしてください"
          />
        }
        description={
          <FormattedMessage
            id="stgAuth.login.description"
            defaultMessage="このステージング環境は、許可された Google アカウントのみ閲覧できます。"
          />
        }
      >
        <GoogleButton onClick={signInWithGoogle}>
          <FormattedMessage
            id="stgAuth.login.button"
            defaultMessage="Google でログイン"
          />
        </GoogleButton>
      </AuthCard>
    </AuthScreen>
  );
};

export default StgAuthGate;
